import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { Minus, Plus, Lock, RefreshCw } from "lucide-react";

const API = "/api";
const fmt = (n) => `$${n.toFixed(2)}`;

const inputCls =
  "w-full border border-[#1F1F1F] bg-[#0D0D0D] px-4 py-3.5 text-sm text-white placeholder-[#555555] outline-none transition-colors duration-300 focus:border-[#E11D2E]";

function Shell({ children, testId }) {
  return (
    <>
      <Header />
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#050505] px-6 pt-16" data-testid={testId}>
        {children}
      </div>
      <Footer />
    </>
  );
}

export default function OrderPage() {
  const { lookupKey } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [state, setState] = useState("loading");
  const [quantity, setQuantity] = useState(1);
  const [targetUrl, setTargetUrl] = useState("");
  const [email, setEmail] = useState("");
  const [customComments, setCustomComments] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setState("loading");
    axios.get(`${API}/services/${lookupKey}`, { timeout: 15000 })
      .then((res) => { setService(res.data); setState("ready"); })
      .catch((err) => setState(err?.response?.status === 404 ? "notfound" : "error"));
  };

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    setQuantity(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey]);

  if (state === "loading") {
    return (
      <Shell testId="order-loading">
        <div className="w-full max-w-md animate-pulse space-y-4">
          <div className="h-3 w-16 bg-[#1F1F1F]" />
          <div className="h-9 w-2/3 bg-[#1F1F1F]" />
          <div className="h-3 w-24 bg-[#1F1F1F]" />
          <div className="mt-8 h-12 w-full bg-[#0D0D0D]" />
          <div className="h-12 w-full bg-[#0D0D0D]" />
        </div>
        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-[#555555]">Loading price…</p>
      </Shell>
    );
  }

  if (state === "error") {
    return (
      <Shell testId="order-error">
        <h1 className="font-display text-2xl font-800 text-white">Couldn't load this service</h1>
        <p className="mt-3 max-w-sm text-center text-sm text-[#8A8A8A]">Your connection hiccupped. Nothing was charged — try again.</p>
        <button data-testid="order-retry" onClick={load} className="mt-6 flex items-center gap-2 bg-[#E11D2E] px-6 py-3 font-mono text-xs uppercase tracking-[0.14em] text-white hover:bg-[#F13B47]">
          <RefreshCw size={13} /> Retry
        </button>
      </Shell>
    );
  }

  if (state === "notfound") {
    return (
      <Shell testId="order-not-found">
        <h1 className="font-display text-2xl font-800 text-white">Service not found</h1>
        <p className="mt-3 max-w-sm text-center text-sm text-[#8A8A8A]">This service may have been retired or the link is incomplete.</p>
        <button data-testid="order-back-catalog" onClick={() => navigate("/", { state: { scrollTo: "#pricing" } })} className="mt-6 border border-[#1F1F1F] px-6 py-3 font-mono text-xs uppercase tracking-[0.14em] text-white hover:border-[#333333]">
          Back to catalog
        </button>
      </Shell>
    );
  }

  const isBundle = service.is_bundle;
  const platformName = isBundle ? "Targeted bundle" : service.platform_name;
  const total = service.price * (isBundle ? 1 : quantity);
  const totalUnits = isBundle ? null : quantity * service.quantity;
  const requiresComments = !isBundle && !!service.requires_comments;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        quantity: isBundle ? 1 : quantity,
        target_url: targetUrl,
        email,
        origin_url: window.location.origin,
        custom_comments: requiresComments ? customComments : "",
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#050505] pt-16" data-testid="order-page">
        <div className="mx-auto grid max-w-5xl gap-10 px-6 py-16 lg:grid-cols-[1.2fr_1fr] lg:px-10 lg:py-24">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E11D2E]">Order</p>
            <h1 className="mt-4 font-display text-3xl font-800 tracking-tight text-white sm:text-4xl" data-testid="order-service-name">{service.name}</h1>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-[#8A8A8A]">{platformName}</p>
            <p className="mt-4 font-mono text-sm text-white" data-testid="order-unit-price">
              {fmt(service.price)}
              {!isBundle && <span className="text-[#8A8A8A]"> per {service.quantity.toLocaleString("en-US")} {service.unit}</span>}
            </p>
            {service.description && (
              <p data-testid="service-description" className="mt-5 max-w-xl whitespace-pre-wrap border-l-2 border-[#E11D2E] pl-4 text-sm leading-relaxed text-[#B0B0B0]">
                {service.description}
              </p>
            )}

            <form data-testid="order-form" onSubmit={submit} className="mt-10 space-y-6">
              {!isBundle && (
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">
                    Quantity (steps of {service.quantity.toLocaleString("en-US")} {service.unit})
                  </label>
                  <div className="flex items-center border border-[#1F1F1F] bg-[#0D0D0D]">
                    <button type="button" data-testid="quantity-decrease" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="px-4 py-3.5 text-[#8A8A8A] hover:text-white">
                      <Minus size={15} />
                    </button>
                    <div className="flex-1 text-center font-mono text-sm text-white" data-testid="quantity-display">
                      {totalUnits.toLocaleString("en-US")}
                    </div>
                    <button type="button" data-testid="quantity-increase" onClick={() => setQuantity((q) => Math.min(500, q + 1))} className="px-4 py-3.5 text-[#8A8A8A] hover:text-white">
                      <Plus size={15} />
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="order-target-url" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">
                  Profile / post / track URL
                </label>
                <input id="order-target-url" data-testid="order-url-input" required minLength={4} value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="https://…" className={inputCls} />
                <p className="mt-2 font-mono text-[11px] text-[#555555]">Public link only. We never ask for your password.</p>
              </div>
              {requiresComments && (
                <div>
                  <label htmlFor="order-comments" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">
                    What should the comments say?
                  </label>
                  <textarea
                    id="order-comments"
                    data-testid="order-comments-input"
                    required
                    minLength={3}
                    maxLength={3000}
                    rows={5}
                    value={customComments}
                    onChange={(e) => setCustomComments(e.target.value)}
                    placeholder={"One comment per line, e.g.\nThis track is fire\nOn repeat all week\nNeed this on my playlist"}
                    className={`${inputCls} resize-y`}
                  />
                  <p className="mt-2 flex justify-between font-mono text-[11px] text-[#555555]">
                    <span>One per line. Leave blank lines out — we rotate through your list.</span>
                    <span data-testid="order-comments-count">{customComments.length}/3000</span>
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="order-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">Email for order updates</label>
                <input id="order-email" data-testid="order-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@brand.com" className={inputCls} />
              </div>
              <button
                data-testid="order-pay-button"
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 bg-[#E11D2E] py-4 font-mono text-xs font-600 uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-[#F13B47] hover:shadow-[0_0_32px_rgba(225,29,46,0.4)] disabled:opacity-50"
              >
                <Lock size={13} /> {submitting ? "Redirecting to checkout…" : `Pay ${fmt(total)}`}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="border border-[#1F1F1F] bg-[#0D0D0D] p-6 lg:sticky lg:top-24" data-testid="order-summary">
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#8A8A8A]">Summary</p>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-[#8A8A8A]">Service</span><span className="text-white">{service.name}</span></div>
                <div className="flex justify-between"><span className="text-[#8A8A8A]">Platform</span><span className="text-white">{platformName}</span></div>
                {!isBundle && (
                  <div className="flex justify-between"><span className="text-[#8A8A8A]">Rate</span><span className="font-mono text-white">{fmt(service.price)} / {service.quantity.toLocaleString("en-US")}</span></div>
                )}
                {!isBundle && (
                  <div className="flex justify-between"><span className="text-[#8A8A8A]">Quantity</span><span className="font-mono text-white">{totalUnits.toLocaleString("en-US")}</span></div>
                )}
                <div className="flex justify-between border-t border-[#1F1F1F] pt-3">
                  <span className="text-[#8A8A8A]">Total</span>
                  <span className="font-mono text-lg font-600 text-white" data-testid="order-total">{fmt(total)}</span>
                </div>
              </div>
              <p className="mt-6 font-mono text-[11px] leading-relaxed text-[#555555]">
                Secure checkout by Stripe. No account needed. Delivery typically starts within 0–6 hours. Refill policy applies per service.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
      <Footer />
    </>
  );
}
